import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useTargetUidPayload } from './useTargetUidPayload';

export type VoiceSource = 'personal' | 'community' | 'all';

export interface ElevenLabsVoiceSummary {
  voice_id: string;
  name: string;
  category?: string;
  labels?: Record<string, string>;
  preview_url?: string;
  description?: string;
  source?: 'personal' | 'community' | 'workspace' | 'cloned';
  owner_id?: string;
  language?: string;
}

export interface UseElevenLabsVoicesOptions {
  source?: VoiceSource;
  /** ISO 639-1 like 'de', 'en'. Empty = no filter. */
  language?: string;
}

export function useElevenLabsVoices(options: UseElevenLabsVoicesOptions = {}) {
  const { user } = useUser();
  const source = options.source ?? 'personal';
  const language = options.language ?? '';
  const adminPayload = useTargetUidPayload();
  return useQuery({
    queryKey: ['elevenlabs-voices', source, language, adminPayload.uid ?? null] as const,
    enabled: !!user,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const fn = httpsCallable<
        { source: VoiceSource; language: string; uid?: string },
        { voices: ElevenLabsVoiceSummary[] }
      >(functions, 'listElevenLabsVoices');
      const r = await fn({ source, language, ...adminPayload });
      return r.data.voices;
    },
  });
}

export function useAddSharedVoice() {
  const qc = useQueryClient();
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: { ownerId: string; voiceId: string; newName: string }) => {
      const fn = httpsCallable<typeof input & { uid?: string }, { voiceId: string }>(
        functions,
        'addSharedVoice',
      );
      const r = await fn({ ...input, ...adminPayload });
      return r.data.voiceId;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['elevenlabs-voices'] });
    },
  });
}
